import { apiClient } from '@shared/api';
import type { ApiResponse } from '@shared/types';
import type {
  ActivityFeedPage,
  ActivityLogDto,
  GetActivityRequest,
  GetEntityActivityRequest,
} from '../types';
import { ENTITY_ACTIVITY_ROUTES, type ActivityEntityType } from './entityRoutes';

class ActivityService {
  /**
   * The cross-module admin feed. Keyset-paged for the same reason the panel is: `occurredAtUtc` is FACT
   * time, so a row can be inserted BELOW a page the client already holds and OFFSET would hand it back a
   * second time as a duplicate React key.
   *
   * Omit the cursor for the first page — that is also the only response that carries `totalCount`.
   */
  async getPaged(filter: GetActivityRequest): Promise<ActivityFeedPage> {
    const { data } = await apiClient.get<ApiResponse<ActivityFeedPage>>('/activity', { params: filter });
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch activity log');
  }

  async getById(id: number): Promise<ActivityLogDto> {
    const { data } = await apiClient.get<ApiResponse<ActivityLogDto>>(
      `/activity/${id}`
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch activity log entry');
  }

  /**
   * One keyset page of a single record's feed (E9 G2). Replaces the per-module `getForPicklist` /
   * `getForReceiving` pair — the URL comes from the route registry, so a new mount is a registry entry
   * rather than another method here.
   *
   * Auth rides on the PARENT document's view rule, which is why the endpoint stays nested on that module's
   * controller. See `entityRoutes.ts` for the full reasoning.
   *
   * ⚠️ Omit the cursor to ask for the first page — that is also the only request whose response carries
   * `totalCount` and `kindCounts`, which the caller holds for the session (D-E).
   */
  async getForEntity(
    entityType: ActivityEntityType,
    entityId: string | number,
    request: GetEntityActivityRequest = {},
  ): Promise<ActivityFeedPage> {
    const { data } = await apiClient.get<ApiResponse<ActivityFeedPage>>(
      ENTITY_ACTIVITY_ROUTES[entityType](entityId),
      {
        params: request,
        // Axios serializes an array as `kinds[]=a&kinds[]=b` by default, which ASP.NET binds to a
        // parameter named `kinds[]` and therefore leaves `Kinds` null — a filter that silently does
        // nothing. `repeat` produces `kinds=a&kinds=b`, which the default model binder reads.
        paramsSerializer: { indexes: null },
      },
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || `Failed to fetch ${entityType.toLowerCase()} activity`);
  }
}

export const activityService = new ActivityService();
